import { useState } from "react";
import { Field, inputClass } from "../routes/SignIn.js";
import { cn, PrimaryButton } from "./ui.js";

/**
 * Step 4 — account creation. Standard Cognito email + password (magic links
 * stay reserved for quick quote replies; every pro has a real account). The
 * email/mobile are pre-filled from the reviewed profile so this is near-instant.
 */
export interface AccountInput {
  contactName: string;
  email: string;
  mobile: string;
  password: string;
}

export function AccountStep({
  initialEmail,
  initialMobile,
  onSubmit,
  submitting,
  error,
}: {
  initialEmail: string;
  initialMobile: string;
  onSubmit: (input: AccountInput) => void;
  submitting: boolean;
  error: string | null;
}) {
  const [contactName, setContactName] = useState("");
  const [email, setEmail] = useState(initialEmail);
  const [mobile, setMobile] = useState(initialMobile);
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const canSubmit =
    contactName.trim() && email.trim() && mobile.trim() && password.length >= 8 && !submitting;

  function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    onSubmit({
      contactName: contactName.trim(),
      email: email.trim(),
      mobile: mobile.trim(),
      password,
    });
  }

  return (
    <main className="mx-auto max-w-md px-6 py-8">
      <h1 className="text-2xl font-bold text-charcoal">Create your account</h1>
      <p className="mt-2 text-sm text-charcoal">
        Almost done — this is how you&rsquo;ll sign in and get job leads.
      </p>

      <form className="mt-7 space-y-5" onSubmit={submit} noValidate>
        <Field label="Your name">
          <input
            className={inputClass}
            value={contactName}
            onChange={(e) => setContactName(e.target.value)}
            autoComplete="name"
            required
          />
        </Field>
        <Field label="Email">
          <input
            className={inputClass}
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            inputMode="email"
            autoComplete="email"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            required
          />
        </Field>
        <Field label="Mobile number">
          <input
            className={inputClass}
            type="tel"
            value={mobile}
            onChange={(e) => setMobile(e.target.value)}
            inputMode="tel"
            autoComplete="tel"
            required
          />
        </Field>
        <Field label="Password">
          <div className="relative">
            <input
              className={cn(inputClass, "pr-16")}
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
              required
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              className="absolute inset-y-0 right-3 my-auto h-8 px-2 text-sm font-medium text-brand-700"
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              {showPassword ? "Hide" : "Show"}
            </button>
          </div>
          <p className="mt-1 text-xs text-charcoal">
            At least 8 characters with upper and lower case letters and a number.
          </p>
        </Field>

        {error && (
          <p role="alert" className="text-sm text-red-600">
            {error}
          </p>
        )}

        <PrimaryButton type="submit" disabled={!canSubmit}>
          {submitting ? "Creating account…" : "Create account"}
        </PrimaryButton>
      </form>
    </main>
  );
}

export function ConfirmStep({
  email,
  onConfirm,
  onResend,
  submitting,
  error,
  info,
}: {
  email: string;
  onConfirm: (code: string) => void;
  onResend: () => void;
  submitting: boolean;
  error: string | null;
  info: string | null;
}) {
  const [code, setCode] = useState("");

  function submit(e: React.FormEvent) {
    e.preventDefault();
    if (code.length >= 4) onConfirm(code);
  }

  return (
    <main className="mx-auto max-w-md px-6 py-10">
      <h1 className="text-center text-2xl font-bold text-charcoal">Verify your email</h1>
      <p className="mt-2 text-center text-sm text-charcoal">
        Enter the 6-digit code we just emailed to {email || "your inbox"}.
      </p>

      <form className="mt-8 space-y-5" onSubmit={submit} noValidate>
        <Field label="Verification code">
          <input
            className={cn(inputClass, "tracking-[0.4em]")}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
            inputMode="numeric"
            pattern="[0-9]*"
            autoComplete="one-time-code"
            maxLength={6}
            autoFocus
            required
          />
        </Field>

        {error && (
          <p role="alert" className="text-sm text-red-600">
            {error}
          </p>
        )}
        {info && <p className="text-sm text-brand-700">{info}</p>}

        <PrimaryButton type="submit" disabled={submitting || code.length < 4}>
          {submitting ? "Verifying…" : "Verify & continue"}
        </PrimaryButton>
        <button
          type="button"
          onClick={onResend}
          className="block w-full min-h-[44px] text-sm font-medium text-brand-700"
        >
          Resend code
        </button>
      </form>
    </main>
  );
}
